import React from 'react';
import { styled } from '@mui/material';

import { useAuth } from 'contexts/auth';
import { useUser2 } from 'contexts/user2';
import { useViewport } from 'contexts/viewport';
import groupService from 'services/group';
import { Avatar, Divider, IconButton, ResponsiveDialog, Stack, Text } from 'shell/components';
import { getUserPreferredFullName } from 'utils/helper';

const ManageMembersDialog = ({
    activeGroupId,
    channels,
    setChannels,
    activeChatId,
    manageMembersDialog,
    setManageMembersDialog,
}) => {
    const { auth } = useAuth();
    const { accountUsers, instructorAccountUsers } = useUser2();
    const { device } = useViewport();

    const handleClose = () => setManageMembersDialog({ open: false });

    const getChannelMembers = () => {
        if (!channels) return [];
        const activeChannel = channels.find(({ id }) => id === activeChatId);
        if (!activeChannel) return [];
        return activeChannel.members
            .filter((id) => Boolean(id))
            .map((id) => {
                const accountUser = accountUsers.find(({ owner_account_id }) => owner_account_id === id);
                return accountUser;
            });
    };

    const addChannelMember = async (accountUser) => {
        setChannels((current) =>
            current.map((channel) => {
                if (channel.id !== activeChatId) return channel;
                return { ...channel, members: [...channel.members, accountUser.owner_account_id] };
            })
        );
        const activeChannel = channels.find(({ id }) => id === activeChatId);
        await groupService.updateChannel(activeGroupId, {
            id: activeChatId,
            members: [...activeChannel.members, accountUser.owner_account_id],
        });
    };

    const removeChannelMember = async (accountUser) => {
        setChannels((current) =>
            current.map((channel) => {
                if (channel.id !== activeChatId) return channel;
                return { ...channel, members: channel.members.filter((id) => id !== accountUser.owner_account_id) };
            })
        );
        const activeChannel = channels.find(({ id }) => id === activeChatId);
        await groupService.updateChannel(activeGroupId, {
            id: activeChatId,
            members: activeChannel.members.filter((id) => id !== accountUser.owner_account_id),
        });
    };

    const currentGroupAccountUsers = accountUsers.filter(({ group_id }) => group_id === activeGroupId);
    const combined = [...currentGroupAccountUsers, ...instructorAccountUsers];

    const channelMembers = getChannelMembers();
    const nonChannelMembers = combined.filter(
        ({ owner_account_id }) =>
            !channelMembers.some((channelMember) => channelMember.owner_account_id === owner_account_id)
    );

    return (
        <ResponsiveDialog open={manageMembersDialog.open} onClose={handleClose}>
            <CustomStack device={device} spacing={4}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'h6'}>Manage Channel Members</Text>
                    <IconButton
                        data-cy={'channel-modal-exit-button'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
                <Stack divider={<Divider />} spacing={4}>
                    <Stack spacing={2}>
                        <Text variant={'p1'}>In this channel ({channelMembers.length})</Text>
                        {channelMembers.map((channelMember) => (
                            <Stack
                                key={channelMember.owner_account_id}
                                alignItems={'center'}
                                direction={'row'}
                                justifyContent={'space-between'}
                            >
                                <Stack
                                    key={channelMember.owner_account_id}
                                    alignItems={'center'}
                                    direction={'row'}
                                    spacing={2}
                                >
                                    <Avatar size={'medium'} user={channelMember} />
                                    <Text variant={'p1'}>{getUserPreferredFullName(channelMember)}</Text>
                                </Stack>
                                <IconButton
                                    disabled={auth.owner_account_id === channelMember.owner_account_id}
                                    kind={'delete'}
                                    size={'medium'}
                                    variant={'square'}
                                    onClick={() => removeChannelMember(channelMember)}
                                />
                            </Stack>
                        ))}
                    </Stack>
                    <Stack spacing={2}>
                        <Text variant={'p1'}>Not in this channel ({nonChannelMembers.length})</Text>
                        {nonChannelMembers.map((nonChannelMember) => (
                            <Stack
                                key={nonChannelMember.owner_account_id}
                                alignItems={'center'}
                                direction={'row'}
                                justifyContent={'space-between'}
                            >
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <Avatar size={'medium'} user={nonChannelMember} />
                                    <Text variant={'p1'}>{getUserPreferredFullName(nonChannelMember)}</Text>
                                </Stack>
                                <IconButton
                                    data-cy={'channel-modal-add-button'}
                                    kind={'add'}
                                    size={'medium'}
                                    variant={'square'}
                                    onClick={() => addChannelMember(nonChannelMember)}
                                />
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            </CustomStack>
        </ResponsiveDialog>
    );
};

const CustomStack = styled(Stack)(({ device }) => {
    return {
        ...(device !== 'mobile' && {
            width: '25rem',
        }),
    };
});

export default ManageMembersDialog;
